import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { Box, Button, Stack, Typography, CircularProgress, Alert, Switch } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { delftBlue, flame } from "@/theme/colors";
import { getSwitchStyles } from "@/utils/switchStyles";
import FormInput from "@/components/forms/FormInput";
import FormTextArea from "@/components/forms/FormTextArea";
import FormSelect from "@/components/forms/FormSelect";
import ActivityCompanyAssignments from "./components/ActivityCompanyAssignments";
import {
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useGetCompaniesQuery,
  useGetPrestartFormsQuery,
  useGetPrestartFormVersionsQuery,
} from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";

const ActivityFrequency = {
  DAILY: "daily",
  MONTHLY: "monthly",
  CUSTOM: "custom",
};

const frequencyOptions = [
  { code: ActivityFrequency.DAILY, name: "Daily" },
  { code: ActivityFrequency.MONTHLY, name: "Monthly" },
  { code: ActivityFrequency.CUSTOM, name: "Custom" },
];

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Activity name is required")
    .min(2, "Activity name must be at least 2 characters")
    .max(100, "Activity name cannot exceed 100 characters"),
  code: Yup.string()
    .required("Activity code is required")
    .min(2, "Activity code must be at least 2 characters")
    .max(20, "Activity code cannot exceed 20 characters")
    .matches(/^[A-Z0-9_-]+$/, "Activity code can only contain uppercase letters, numbers, underscores, and hyphens"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description cannot exceed 1000 characters"),
  logoImageUrl: Yup.string().url("Must be a valid URL").optional(),
  frequency: Yup.string()
    .oneOf([ActivityFrequency.DAILY, ActivityFrequency.MONTHLY, ActivityFrequency.CUSTOM], "Invalid frequency")
    .required("Frequency is required"),
  companyIds: Yup.array()
    .min(1, "At least one company must be selected")
    .required("Company selection is required"),
  formConfigKey: Yup.string()
    .matches(/^[A-Z0-9_-]+$/, "Form config key can only contain uppercase letters, numbers, underscores, and hyphens")
    .optional(),
  formVersion: Yup.string().optional(),
  startDate: Yup.string().when("frequency", {
    is: ActivityFrequency.CUSTOM,
    then: (schema) => schema.required("Start date is required for custom frequency"),
    otherwise: (schema) => schema.optional(),
  }),
  endDate: Yup.string().when("frequency", {
    is: ActivityFrequency.CUSTOM,
    then: (schema) => schema.required("End date is required for custom frequency"),
    otherwise: (schema) => schema.optional(),
  }),
  isActive: Yup.boolean(),
});

const ActivityFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const isEditMode = !!id;

  const { data: activityData, isLoading: isLoadingActivity, error: activityError, refetch: refetchActivity } = useGetActivityQuery(id, {
    skip: !isEditMode,
  });

  // Refetch activity data when navigating to edit form to ensure fresh data
  useEffect(() => {
    if (isEditMode && id) {
      refetchActivity();
    }
  }, [id, isEditMode, refetchActivity]);

  const { data: companiesData, isLoading: isLoadingCompanies } = useGetCompaniesQuery({ page: 1, limit: 10000 });
  const [createActivity, { isLoading: isCreating }] = useCreateActivityMutation();
  const [updateActivity, { isLoading: isUpdating }] = useUpdateActivityMutation();

  const { data: formsResponse } = useGetPrestartFormsQuery(
    { page: 1, limit: 1, activityId: id },
    { skip: !isEditMode || !id }
  );
  const prestartForm = formsResponse?.data?.prestartForms?.[0];
  const prestartFormId = prestartForm?._id || prestartForm?.id;
  const { data: versionsResponse } = useGetPrestartFormVersionsQuery(prestartFormId, {
    skip: !prestartFormId,
  });

  const companies = companiesData?.data?.companies || companiesData?.companies || [];
  const companyOptions = companies.map((company) => {
    const companyId = company._id?.toString() || company.id?.toString() || company._id || company.id;
    return {
      code: companyId,
      name: company.name || company.companyName || "Unknown Company",
    };
  });

  const activity = activityData?.data?.activity || activityData?.activity;

  // Determine which companies can be selected based on user role
  const getAvailableCompanies = () => {
    if (user?.role === Roles.SUPER_ADMIN) {
      return companyOptions;
    } else if (user?.role === Roles.ADMIN && user?.companyId) {
      // Admin can only select their own company
      const userCompanyId = user.companyId.toString();
      return companyOptions.filter((c) => c.code === userCompanyId);
    }
    return [];
  };

  const availableCompanies = getAvailableCompanies();

  // Normalize companyIds from activity (handle both ObjectId objects and strings)
  const normalizeCompanyIds = (companyIds) => {
    if (!companyIds || !Array.isArray(companyIds)) {
      return user?.companyId ? [user.companyId.toString()] : [];
    }
    return companyIds.map((c) => {
      // Handle populated objects with _id (old format)
      if (typeof c === 'object' && c._id !== undefined) {
        return c._id.toString();
      }
      // Handle populated objects with id (new format - transformed by backend)
      if (typeof c === 'object' && c.id !== undefined) {
        return c.id.toString();
      }
      // Handle string IDs
      return c.toString();
    });
  };

  const existingCompanyIds = normalizeCompanyIds(activity?.companyIds);
  const versions = versionsResponse?.data?.versions || [];
  const assignments = versionsResponse?.data?.assignments || [];

  const defaultFormVersion = useMemo(() => {
    const published = assignments.map((row) => Number(row.publishedVersion)).filter((value) => Number.isFinite(value));
    if (published.length) {
      return String(Math.max(...published));
    }
    if (prestartForm?.latestVersion) {
      return String(prestartForm.latestVersion);
    }
    if (versions[0]?.version) {
      return String(versions[0].version);
    }
    return "";
  }, [assignments, prestartForm?.latestVersion, versions]);

  const initialValues = {
    name: activity?.name || "",
    code: activity?.code || "",
    description: activity?.description || "",
    logoImageUrl: activity?.logoImageUrl || "",
    frequency: activity?.frequency || ActivityFrequency.DAILY,
    companyIds: normalizeCompanyIds(activity?.companyIds) || (user?.companyId ? [user.companyId.toString()] : []),
    formConfigKey: activity?.formConfigKey || "",
    startDate: activity?.startDate ? new Date(activity.startDate).toISOString().split('T')[0] : "",
    endDate: activity?.endDate ? new Date(activity.endDate).toISOString().split('T')[0] : "",
    startTime: activity?.startTime || "",
    endTime: activity?.endTime || "",
    isActive: activity?.isActive !== undefined ? activity.isActive : true,
    formVersion: defaultFormVersion,
    newCompanyVersions: {},
  };

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      // Ensure admin's companyId is always set (for admin role, dropdown is hidden)
      let companyIds = values.companyIds;
      if (user?.role === Roles.ADMIN && user?.companyId) {
        if (!companyIds || !Array.isArray(companyIds) || companyIds.length === 0) {
          companyIds = [user.companyId.toString()];
        }
      }

      const payload = {
        name: values.name,
        code: values.code.toUpperCase(),
        description: values.description,
        logoImageUrl: values.logoImageUrl || "",
        frequency: values.frequency,
        companyIds: companyIds,
        formConfigKey: values.formConfigKey ? values.formConfigKey.toUpperCase() : undefined,
        isActive: values.isActive,
      };

      if (values.frequency === ActivityFrequency.CUSTOM) {
        payload.startDate = values.startDate;
        payload.endDate = values.endDate;
      }

      if (isEditMode && prestartFormId) {
        const addedCompanyIds = (companyIds || []).filter((companyId) => !existingCompanyIds.includes(companyId));
        if (addedCompanyIds.length > 0) {
          const versionsForNew = addedCompanyIds.map(
            (companyId) => values.newCompanyVersions?.[companyId] || values.formVersion
          );
          if (versionsForNew.some((version) => !version)) {
            setStatus({
              success: false,
              message: "Select a form version when adding a company",
            });
            setSubmitting(false);
            return;
          }

          const groups = {};
          addedCompanyIds.forEach((companyId, index) => {
            const version = String(versionsForNew[index]);
            if (!groups[version]) groups[version] = [];
            groups[version].push(companyId);
          });

          const keptIds = (companyIds || []).filter((companyId) => existingCompanyIds.includes(companyId));
          let currentIds = keptIds;
          const versionKeys = Object.keys(groups);

          for (let i = 0; i < versionKeys.length; i += 1) {
            const version = versionKeys[i];
            currentIds = [...currentIds, ...groups[version]];
            const { error } = await updateActivity({
              id,
              ...payload,
              companyIds: currentIds,
              formVersion: Number(version),
            });
            if (error) {
              setStatus({ success: false, message: error?.data?.message || "Failed to update activity" });
              setSubmitting(false);
              return;
            }
          }

          navigate("/activities", { state: { refresh: true } });
          return;
        }
      }

      if (isEditMode) {
        const { error } = await updateActivity({ id, ...payload });
        if (error) {
          setStatus({ success: false, message: error?.data?.message || "Failed to update activity" });
          setSubmitting(false);
          return;
        }
      } else {
        const { error } = await createActivity(payload);
        if (error) {
          setStatus({ success: false, message: error?.data?.message || "Failed to create activity" });
          setSubmitting(false);
          return;
        }
      }

      navigate("/activities", { state: { refresh: true } });
    } catch (error) {
      setStatus({ success: false, message: error.message || "An error occurred" });
      setSubmitting(false);
    }
  };

  if (isEditMode && isLoadingActivity) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isEditMode && activityError) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">{activityError?.data?.message || "Failed to load activity"}</Alert>
        <Button onClick={() => navigate("/activities")} sx={{ mt: 2 }}>
          Back to Activities
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ handleSubmit, isSubmitting, status, values, setFieldValue, errors, touched }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Header */}
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 22, sm: 24, md: 28 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.29,
                    marginBottom: 1,
                  }}
                >
                  {isEditMode ? "Edit Activity" : "Add New Activity"}
                </Typography>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 13, sm: 14, md: 16 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  {isEditMode ? "Update activity details" : "Create a new activity"}
                </Typography>
              </Box>

              {/* Error Message */}
              {status?.message && (
                <Alert severity="error">{status.message}</Alert>
              )}

              {/* Form Fields */}
              <Stack spacing={2}>
                <FormInput name="name" label="Activity Name" placeholder="e.g., Kayak Tour" />
                <FormInput name="code" label="Activity Code" placeholder="e.g., KAYAK_TOUR_001" />
                <FormTextArea name="description" label="Description" placeholder="Enter activity description" />
                <FormInput name="logoImageUrl" label="Logo Image URL" placeholder="https://example.com/logo.png" />

                <FormSelect
                  name="frequency"
                  label="Frequency"
                  items={frequencyOptions}
                />

                {values.frequency === ActivityFrequency.CUSTOM && (
                  <>
                    <FormInput name="startDate" label="Start Date" type="date" />
                    <FormInput name="endDate" label="End Date" type="date" />
                  </>
                )}

                {/* Companies dropdown - hidden for admin role */}
                {user?.role === Roles.SUPER_ADMIN && (
                  isLoadingCompanies ? (
                    <CircularProgress size={24} />
                  ) : (
                    <ActivityCompanyAssignments
                      companyOptions={availableCompanies}
                      selectedIds={values.companyIds || []}
                      onChange={(nextIds) => setFieldValue("companyIds", nextIds)}
                      assignments={assignments}
                      versions={versions}
                      newCompanyVersions={values.newCompanyVersions || {}}
                      onNewCompanyVersionsChange={(next) => setFieldValue("newCompanyVersions", next)}
                      hasTemplate={Boolean(isEditMode && prestartFormId)}
                      defaultFormVersion={values.formVersion || defaultFormVersion}
                      error={touched.companyIds && errors.companyIds ? errors.companyIds : undefined}
                    />
                  )
                )}

                {user?.role === Roles.SUPER_ADMIN && isEditMode && !prestartFormId && (
                  <Alert severity="info">
                    This activity has no form template yet. Companies will get access to the activity only.
                    Create a template under Forms, then you can choose a version when adding companies.
                  </Alert>
                )}

                <FormInput name="formConfigKey" label="Form Config Key (Optional)" placeholder="e.g., KAYAK_TOUR" />

                {/* Status field - hidden for admin role */}
                {user?.role === Roles.SUPER_ADMIN && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingY: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                      Status
                    </Typography>
                    <Switch
                      checked={values.isActive}
                      onChange={(e) => setFieldValue("isActive", e.target.checked)}
                      sx={getSwitchStyles(false)}
                    />
                  </Box>
                )}
              </Stack>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => navigate("/activities")}
                  disabled={isSubmitting || isCreating || isUpdating}
                >
                  Cancel
                </Button>
                {/* Update button - only visible to super admin in edit mode, Create button visible to both admin and super admin */}
                {isEditMode && user?.role !== Roles.SUPER_ADMIN ? null : (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || isCreating || isUpdating}
                    sx={{
                      backgroundColor: flame[500],
                      "&:hover": {
                        backgroundColor: flame[600],
                      },
                    }}
                  >
                    {isSubmitting || isCreating || isUpdating ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      isEditMode ? "Update Activity" : "Create Activity"
                    )}
                  </Button>
                )}
              </Stack>
            </Stack>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default ActivityFormPage;

